import { FC, useId, useMemo, useState } from 'react';
import { formatDateTime } from '@libs/frontend-services';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc, NoteOrigin, RouterOutput } from 'src/trpc';

type OrderNote = RouterOutput['admin']['note']['getNotes']['notes'][number];

/**
 * Promoted order-detail Notes: a plain framed composer with a Post button and a
 * colored-dot activity feed with bold, dark actor names. It reads and writes through
 * the same note endpoints as the shared `Notes` component, but is a separate component
 * so the order page can carry the promoted treatment without restyling the shared one
 * still used by the driver/truck/container pages.
 *
 * Deliberately does NOT use the mockup's orange callout border/background on the
 * composer - the frame is the neutral card border used elsewhere in the admin UI.
 */
export const PromotedNotes: FC<{ entityId: string; origin: NoteOrigin }> = ({
  entityId,
  origin,
}) => {
  const [noteContent, setNoteContent] = useState('');
  const notesTextareaId = useId();
  const utils = trpc.useUtils();
  const { data } = trpc.admin.note.getNotes.useQuery({ entityId, origin });
  const { mutate, isPending } = trpc.admin.note.addNote.useMutation({
    onSuccess: () => {
      utils.admin.note.getNotes.invalidate();
    },
  });

  /**
   * Field-level correction records render in the Activity drawer's Activity Log, leaving this
   * panel as the driver-critical feed of manual comments. Use metadata rather than note content
   * so ordinary comments cannot be hidden accidentally.
   */
  const notes = useMemo(
    () => (data?.notes || []).filter((note: OrderNote) => !note.correctionType),
    [data],
  );

  const authorIds = useMemo(
    () => Array.from(new Set(notes.map((note) => note.createdBy).filter(Boolean))),
    [notes],
  );

  const { data: authors = [] } = trpc.admin.user.getUsersByIds.useQuery(authorIds, {
    enabled: authorIds.length > 0,
  });

  const authorNameMap = useMemo(() => {
    const map = new Map<string, string>();
    authors.forEach((user) => {
      if (user && user._id) {
        const fullName = `${user.name?.first || ''} ${user.name?.last || ''}`.trim();
        map.set(user._id.toString(), fullName || user.email || 'Unknown User');
      }
    });
    return map;
  }, [authors]);

  return (
    <div className="flex flex-col gap-4 w-full">
      {/* Plain framed composer - neutral border, not the mockup's orange callout */}
      <div className="flex flex-col gap-3 bg-white border-disabled border rounded-lg p-4 w-full">
        <div className="flex flex-col">
          <Label htmlFor={notesTextareaId} className="label mb-2 text-sm">
            Notes
          </Label>
          <Textarea
            id={notesTextareaId}
            placeholder="Leave a comment..."
            className="placeholder:text-text-light text-text-primary"
            onChange={(e) => setNoteContent(e.target.value)}
            value={noteContent}
          />
        </div>
        <div className="flex flex-row justify-end">
          <Button
            onClick={() => {
              mutate({ content: noteContent, entityId, origin });
              setNoteContent('');
            }}
            className="text-white"
            // Clearing the textarea disables the button on the next render, but a second click
            // landing before that render would post the same note twice. The in-flight check
            // closes that window.
            disabled={!noteContent || isPending}
          >
            Post
          </Button>
        </div>
      </div>

      {/* Colored-dot activity feed with bold, dark actor names */}
      <div className="flex flex-col gap-5">
        {notes.map((note: OrderNote, ind: number) => (
          <div className="flex flex-row items-start gap-3" key={String(note._id)}>
            <span
              className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                ind === 0 ? 'bg-primary' : 'bg-slate-300'
              }`}
            />
            {/* Below `sm` the timestamp drops under the note body rather than competing with
                it for width - a nowrap date beside 21px text squeezes the note down to a few
                characters per line. It stays in the same row so it still reads as that note's
                timestamp. */}
            <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-start sm:gap-3">
              {/* `overflow-wrap: anywhere`, not `break-words`. `break-words` leaves the
                  min-content width at the longest unbroken word, so a correction note kept a
                  223px floor inside a 160px column and scrolled sideways. `anywhere` also
                  shrinks min-content, which is what lets `min-w-0` actually hold. */}
              <div className="flex-1 min-w-0 text-sm text-text-light leading-snug [overflow-wrap:anywhere]">
                {note.createdBy && (
                  <span className="font-semibold text-text-primary">
                    {authorNameMap.get(note.createdBy) || 'Unknown User'}
                  </span>
                )}{' '}
                {note.content}
              </div>
              <div className="text-xs text-text-disabled whitespace-normal sm:whitespace-nowrap">
                {/* formatDateTime, not a bare date-fns format: an absent or malformed
                    createdAt would otherwise throw RangeError and blank the whole page. */}
                {formatDateTime(note.createdAt, "MMM d, yyyy 'at' p")}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
